import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { documentAPI, companyAPI } from '../../services/api';
import { buildTruckReceiptData } from '../PDF/helpers';

// Edit form for the data printed onto the pre-printed JAIN PARIVAHAN
// lorry receipt. Prefilled from the saved truck receipt if one exists,
// otherwise from defaults derived from the commercial invoice.
export default function TruckReceiptEditModal({ docId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [invoiceDefaults, setInvoiceDefaults] = useState(null);

  const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const docRes = await documentAPI.getById(docId);
        const fullDoc = docRes.data;
        let company = {};
        try {
          const compRes = await companyAPI.getById(fullDoc.company_id);
          company = compRes.data || {};
        } catch (_) { /* defaults still work without company */ }

        const data = buildTruckReceiptData(fullDoc, company);
        // Pure invoice-derived values, ignoring any saved truck receipt —
        // used by the "Reset from Invoice" button.
        const fresh = buildTruckReceiptData(
          { ...fullDoc, truckReceipt: null, truck_receipt: null },
          company
        );
        if (!cancelled) {
          setForm({
            ...data,
            consignment_date: toDateInput(data.consignment_date),
            policy_date: toDateInput(data.policy_date),
          });
          setInvoiceDefaults({
            ...fresh,
            consignment_date: toDateInput(fresh.consignment_date),
            policy_date: toDateInput(fresh.policy_date),
          });
        }
      } catch (err) {
        console.error('Failed to load truck receipt:', err);
        toast.error(err.response?.data?.error || 'Failed to load truck receipt');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [docId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async (preview) => {
    setSaving(true);
    try {
      await documentAPI.saveTruckReceipt(docId, {
        ...form,
        consignment_date: form.consignment_date || null,
        policy_date: form.policy_date || null,
      });
      toast.success('Truck receipt saved');
      onClose();
      if (preview && onSaved) onSaved();
    } catch (err) {
      console.error('Failed to save truck receipt:', err);
      toast.error(err.response?.data?.error || 'Failed to save truck receipt');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: '#047857',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };
  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: 8,
    fontSize: 13,
    color: '#1f2937',
    fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
    background: '#fff',
  };
  const fieldFull = { gridColumn: '1 / -1' };

  const text = (label, key, props = {}) => (
    <div style={props.full ? fieldFull : undefined}>
      <label style={labelStyle}>{label}</label>
      <input
        type={props.type || 'text'}
        value={form[key] || ''}
        onChange={set(key)}
        style={inputStyle}
      />
    </div>
  );
  const area = (label, key, rows = 2) => (
    <div style={fieldFull}>
      <label style={labelStyle}>{label}</label>
      <textarea
        rows={rows}
        value={form[key] || ''}
        onChange={set(key)}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(6,78,59,0.25)',
            width: '100%',
            maxWidth: 640,
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '18px 24px',
            borderBottom: '1px solid rgba(16,185,129,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#064e3b', fontFamily: "'Poppins', sans-serif" }}>
                🧾 Edit Truck Receipt
              </div>
              <div style={{ fontSize: 12, color: '#047857', marginTop: 2 }}>
                Data printed onto the pre-printed lorry receipt form
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(6,78,59,0.08)',
                border: 'none',
                borderRadius: 8,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#064e3b',
                fontSize: 18,
              }}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: 20, overflowY: 'auto' }}>
            {loading || !form ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                Loading truck receipt…
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {text('Consignment Note No.', 'consignment_note_no')}
                {text('Consignment Date', 'consignment_date', { type: 'date' })}
                {text('From', 'from_place')}
                {text('To', 'to_place')}
                {area('Address of Delivery Office', 'delivery_office')}
                {text('State', 'state')}
                {text('Tel No.', 'tel_no')}
                {area("Consignor's Name & Address", 'consignor')}
                {area("Consignee Bank's Name & Address", 'consignee_bank')}
                {area('Notify (printed after bank details)', 'notify')}
                {area('Description (Said to Contain)', 'description', 4)}
                {text('Packages (e.g. 104 DRUMS.)', 'packages')}
                {text('Value', 'value')}
                {text('Total Net Weight', 'actual_weight')}
                {text('Total Gross Weight', 'charged_weight')}

                <div style={{ ...fieldFull, display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <input
                    id="tr-insured"
                    type="checkbox"
                    checked={!!form.insured}
                    onChange={set('insured')}
                    style={{ width: 16, height: 16, accentColor: '#10b981', cursor: 'pointer' }}
                  />
                  <label htmlFor="tr-insured" style={{ fontSize: 13, fontWeight: 600, color: '#064e3b', cursor: 'pointer' }}>
                    Consignor has insured the consignment
                  </label>
                </div>

                {form.insured && (
                  <>
                    {text('Insurance Company', 'insurance_company')}
                    {text('Policy No.', 'policy_no')}
                    {text('Policy Date', 'policy_date', { type: 'date' })}
                    {text('Insured Amount', 'insurance_amount')}
                    {text('Risk', 'risk')}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '14px 24px',
            borderTop: '1px solid rgba(16,185,129,0.15)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: '#f8fdfb',
          }}>
            <button
              onClick={() => invoiceDefaults && setForm(invoiceDefaults)}
              disabled={saving || loading || !invoiceDefaults}
              title="Refill every field from the invoice data (discards edits until you save)"
              style={{
                marginRight: 'auto',
                padding: '9px 14px',
                borderRadius: 8,
                border: '1px solid rgba(16,185,129,0.3)',
                background: '#fff',
                color: '#047857',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ↺ Reset from Invoice
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '9px 18px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving || loading}
              style={{
                padding: '9px 18px',
                borderRadius: 8,
                border: '1px solid rgba(16,185,129,0.4)',
                background: '#ecfdf5',
                color: '#047857',
                fontSize: 13,
                fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              Save & Preview
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving || loading}
              style={{
                padding: '9px 22px',
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer',
                boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
